import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EstadoError, ListaEsqueleto } from '../../components/ui/Estados';
import { Icon } from '../../components/ui/Icon';
import { CabeceraPagina } from '../../components/layout/CabeceraPagina';
import { repositorioAlarmas } from '../../../data/repositories/RepositorioAlarmas';
import type { Alarma } from '../../../domain/entities/Alarma';
import type { ColorMarcador } from '../../../domain/entities/PanelPersonal';
import type { Sensor } from '../../../domain/entities/Sensor';
import { useTiempoReal } from '../../hooks/useTiempoReal';
import { formatearAntiguedad } from '../../utils/formato';
import { SelectorSensor } from './SelectorSensor';
import { usePanel } from './usePanel';
import estilos from './Panel.module.css';

/**
 * Paleta de los marcadores, idéntica a la de la versión anterior.
 * Un marcador cuyo sensor está en alarma se pinta rojo sea cual sea su color.
 */
const COLOR_MARCADOR: Record<ColorMarcador, { hex: string; fondo: string }> = {
  verde: { hex: '#34D399', fondo: 'rgba(52,211,153,0.3)' },
  naranja: { hex: '#F5A623', fondo: 'rgba(245,166,35,0.3)' },
  rojo: { hex: '#EF4444', fondo: 'rgba(239,68,68,0.3)' },
  azul: { hex: '#3B82F6', fondo: 'rgba(59,130,246,0.3)' },
};

/** Cómo se pinta cada criticidad en la actividad reciente. */
const CRITICIDAD_FEED: Record<
  string,
  { icono: 'advertencia' | 'info' | 'circulo'; fondo: string; color: string; clase: string }
> = {
  critica: {
    icono: 'advertencia',
    fondo: 'rgba(239,68,68,0.2)',
    color: '#EF4444',
    clase: 'critCritica',
  },
  alta: {
    icono: 'advertencia',
    fondo: 'rgba(239,68,68,0.15)',
    color: '#EF4444',
    clase: 'critAlta',
  },
  media: {
    icono: 'advertencia',
    fondo: 'rgba(245,158,11,0.2)',
    color: '#F59E0B',
    clase: 'critMedia',
  },
  baja: { icono: 'info', fondo: 'rgba(59,130,246,0.2)', color: '#3B82F6', clase: 'critBaja' },
};

const CRITICIDAD_FEED_DEFECTO = {
  icono: 'circulo' as const,
  fondo: 'rgba(148,163,184,0.2)',
  color: '#64748B',
  clase: '',
};

/** Qué elemento está eligiendo sensor. */
type Objetivo = { tipo: 'marcador' | 'tarjeta'; id: string } | null;

/** Valor en vivo de un sensor, con su estado, tal como lo pinta el panel. */
function valorEnVivo(sensor: Sensor | null): {
  texto: string;
  enAlarma: boolean;
  conDatos: boolean;
} {
  if (!sensor) return { texto: 'Sin datos', enAlarma: false, conDatos: false };

  const texto = sensor.valorMostrable;
  if (texto === '—') return { texto: 'Sin datos', enAlarma: false, conDatos: false };

  return { texto, enAlarma: sensor.estado.valor === 'critico', conDatos: true };
}

export function Panel() {
  const p = usePanel();
  const clienteConsultas = useQueryClient();

  const [objetivo, setObjetivo] = useState<Objetivo>(null);
  const [globoAbierto, setGloboAbierto] = useState<string | null>(null);
  const [arrastrando, setArrastrando] = useState<string | null>(null);
  const [catalogoAbierto, setCatalogoAbierto] = useState(false);
  const [feedPlegado, setFeedPlegado] = useState(false);
  const [sello, setSello] = useState<'oculto' | 'guardado' | 'error'>('oculto');

  const refPlano = useRef<HTMLDivElement>(null);
  const refArchivo = useRef<HTMLInputElement>(null);
  const refSelloTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Reloj del topbar, como el original: HH:MM refrescado cada 30 s. ──
  const [hora, setHora] = useState(() => new Date());
  useEffect(() => {
    const temporizador = window.setInterval(() => setHora(new Date()), 30_000);
    return () => window.clearInterval(temporizador);
  }, []);

  const reloj = `${String(hora.getHours()).padStart(2, '0')}:${String(hora.getMinutes()).padStart(2, '0')}`;

  // ── Actividad reciente: las alarmas abiertas más nuevas, en vivo. ──
  const alarmas = useQuery({
    queryKey: ['panel', 'alarmas-feed'],
    queryFn: () => repositorioAlarmas.listar({ estado: 'abiertas', pagina: 1, porPagina: 8 }),
  });

  useTiempoReal({ tabla: 'alarmas', evento: '*' }, () => {
    void clienteConsultas.invalidateQueries({ queryKey: ['panel', 'alarmas-feed'] });
  });

  const feed: readonly Alarma[] = alarmas.data?.filas ?? [];

  // ── Salir de edición guarda y muestra el sello «Guardado ✓». ──
  const terminarEdicion = useCallback(async () => {
    p.alternarEdicion();
    setCatalogoAbierto(false);

    const ok = await p.guardarAhora();
    setSello(ok ? 'guardado' : 'error');

    if (refSelloTimer.current) clearTimeout(refSelloTimer.current);
    refSelloTimer.current = setTimeout(() => setSello('oculto'), ok ? 2000 : 4000);
  }, [p]);

  useEffect(() => {
    return () => {
      if (refSelloTimer.current) clearTimeout(refSelloTimer.current);
    };
  }, []);

  // ── Arrastre de marcadores, acotado al 2–98 % como el original. ──
  const posicionRelativa = useCallback((e: PointerEvent) => {
    const caja = refPlano.current?.getBoundingClientRect();
    if (!caja || caja.width === 0 || caja.height === 0) return null;

    return {
      x: Math.min(98, Math.max(2, ((e.clientX - caja.left) / caja.width) * 100)),
      y: Math.min(98, Math.max(2, ((e.clientY - caja.top) / caja.height) * 100)),
    };
  }, []);

  const alMoverMarcador = useCallback(
    (e: PointerEvent) => {
      if (!arrastrando) return;
      const posicion = posicionRelativa(e);
      if (posicion) p.cambiar((actual) => actual.actualizarMarcador(arrastrando, posicion));
    },
    [arrastrando, posicionRelativa, p],
  );

  const elegirSensor = useCallback(
    (sensor: Sensor) => {
      if (!objetivo) return;

      if (objetivo.tipo === 'marcador') {
        p.cambiar((actual) =>
          actual.actualizarMarcador(objetivo.id, {
            sensorId: sensor.id,
            sensorNombre: sensor.nombre,
            sensorTipo: sensor.tipoNombre ?? '',
            sensorArea: sensor.areaNombre ?? '',
            sensorUnidad: sensor.unidadMostrable,
          }),
        );
      } else {
        p.cambiar((actual) =>
          actual.actualizarTarjeta(objetivo.id, {
            sensorId: sensor.id,
            etiqueta: sensor.nombre,
            descripcion: [sensor.areaNombre, sensor.nodoNombre].filter(Boolean).join(' · '),
            unidad: sensor.unidadMostrable,
            tipo: sensor.tipoNombre ?? '',
            area: sensor.areaNombre ?? '',
          }),
        );
      }

      setObjetivo(null);
    },
    [objetivo, p],
  );

  if (p.cargando) return <ListaEsqueleto filas={5} />;
  if (p.error || !p.panel) {
    return <EstadoError titulo="No se pudo cargar tu panel" />;
  }

  const panel = p.panel;
  const clasesRaiz = [
    estilos.raiz,
    panel.imagenUrl ? estilos.conImagen : estilos.sinImagen,
    p.editando ? estilos.editando : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={estilos.pagina}>
      <CabeceraPagina titulo="Panel" descripcion="Tu vista personal de la instalación, en vivo." />

      <div className={clasesRaiz}>
        {/* Fondo: la foto de la instalación, o la invitación a subirla. */}
        <div className={estilos.fondo}>
          {panel.imagenUrl ? (
            <img src={panel.imagenUrl} alt={panel.nombreInstalacion} />
          ) : (
            <div className={estilos.fondoVacio}>
              <div className={estilos.fondoVacioIcono}>🏠</div>
              <div className={estilos.fondoVacioTitulo}>Sin imagen de fondo</div>
              <div className={estilos.fondoVacioTexto}>
                Haz clic en <b>Editar</b> y luego en <b>Cambiar imagen</b> para personalizar
              </div>
            </div>
          )}
          <div className={estilos.velo} />
        </div>

        {/* Topbar interno. */}
        <div className={estilos.topbar}>
          <div className={estilos.topbarIzquierda}>
            <div className={estilos.nombreSitio}>{panel.nombreInstalacion}</div>
            <div className={estilos.etiquetaVivo}>En vivo</div>
          </div>

          <div className={estilos.topbarDerecha}>
            <div
              className={[
                estilos.selloGuardado,
                sello !== 'oculto' ? estilos.selloGuardadoVisible : '',
                sello === 'error' ? estilos.selloGuardadoError : '',
              ]
                .filter(Boolean)
                .join(' ')}
              role="status"
            >
              <Icon nombre={sello === 'error' ? 'advertencia' : 'check'} tamano={14} />
              {sello === 'error' ? 'Error al guardar' : 'Guardado'}
            </div>

            <div className={estilos.reloj}>{reloj}</div>

            {!p.editando && (
              <button
                type="button"
                className={`${estilos.boton} ${estilos.botonContorno}`}
                title="Editar vista"
                onClick={p.alternarEdicion}
              >
                <Icon nombre="editar" tamano={16} />
                <span>Editar</span>
              </button>
            )}

            {p.editando && (
              <button
                type="button"
                className={`${estilos.boton} ${estilos.botonPrimario}`}
                title="Agregar widget"
                onClick={() => setCatalogoAbierto((v) => !v)}
              >
                <Icon nombre="agregar" tamano={16} />
                <span>Widget</span>
              </button>
            )}
          </div>
        </div>

        {/* Banner de modo edición. */}
        {p.editando && (
          <div className={estilos.bannerEdicion}>
            <span className={estilos.puntoEdicion} />
            <span>Modo edición activo</span>
            <span className={estilos.pistaEdicion}>
              · Arrastra pines · <Icon nombre="ajustes" tamano={12} /> elige sensor · ✕ elimina
            </span>
            <div className={estilos.separadorBanner} />
            <button
              type="button"
              className={`${estilos.boton} ${estilos.botonFantasma}`}
              onClick={() => refArchivo.current?.click()}
            >
              <Icon nombre="imagen" tamano={16} /> Cambiar imagen
            </button>
            <button
              type="button"
              className={`${estilos.boton} ${estilos.botonFantasma}`}
              onClick={() => {
                const nombre = window.prompt('Nombre de la instalación:', panel.nombreInstalacion);
                if (nombre?.trim()) p.cambiar((a) => a.conNombre(nombre.trim()));
              }}
            >
              <Icon nombre="editar" tamano={16} /> Renombrar
            </button>
            <button
              type="button"
              className={`${estilos.boton} ${estilos.botonExito}`}
              onClick={() => void terminarEdicion()}
            >
              <Icon nombre="check" tamano={16} /> Listo
            </button>
          </div>
        )}

        {/* Capa de marcadores sobre la imagen. */}
        <div
          ref={refPlano}
          className={estilos.capaMarcadores}
          onPointerMove={alMoverMarcador}
          onPointerUp={() => setArrastrando(null)}
          onPointerLeave={() => setArrastrando(null)}
        >
          {panel.marcadores.map((m) => {
            const sensor = p.sensorDe(m.sensorId);
            const vivo = valorEnVivo(sensor);
            const color = COLOR_MARCADOR[vivo.enAlarma ? 'rojo' : m.color];

            const tituloGlobo = m.sensorId ? m.sensorNombre : 'Sin sensor asignado';
            const textoGlobo = m.sensorId
              ? [vivo.texto, m.sensorArea].filter(Boolean).join(' · ')
              : 'Clic en ⚙ para elegir sensor';

            return (
              <div
                key={m.id}
                className={[
                  estilos.marcador,
                  arrastrando === m.id ? estilos.marcadorArrastrando : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ left: `${m.x}%`, top: `${m.y}%` }}
                role="button"
                tabIndex={0}
                aria-label={tituloGlobo}
                onPointerDown={(e) => {
                  if (!p.editando) return;
                  const objetivoPulsado = e.target as HTMLElement;
                  if (objetivoPulsado.closest('button')) return;
                  e.preventDefault();
                  setArrastrando(m.id);
                }}
                onClick={(e) => {
                  if (p.editando) return;
                  if ((e.target as HTMLElement).closest('button')) return;
                  setGloboAbierto((abierto) => (abierto === m.id ? null : m.id));
                }}
              >
                <div className={estilos.marcadorPulso} style={{ background: color.hex }} />
                <div
                  className={estilos.marcadorPunto}
                  style={{ background: color.fondo, color: color.hex }}
                >
                  ●
                </div>

                {globoAbierto === m.id && !p.editando && (
                  <div className={estilos.globo}>
                    <div className={estilos.globoTitulo}>{tituloGlobo}</div>
                    <div className={estilos.globoTexto}>{textoGlobo}</div>
                  </div>
                )}

                <button
                  type="button"
                  className={estilos.insigniaElegir}
                  title="Elegir sensor"
                  onClick={(e) => {
                    e.stopPropagation();
                    setObjetivo({ tipo: 'marcador', id: m.id });
                  }}
                >
                  <Icon nombre="ajustes" tamano={11} />
                </button>
                <button
                  type="button"
                  className={estilos.insigniaQuitar}
                  title="Eliminar"
                  onClick={(e) => {
                    e.stopPropagation();
                    p.cambiar((a) => a.quitarMarcador(m.id));
                  }}
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>

        <input
          ref={refArchivo}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const archivo = e.target.files?.[0];
            if (archivo) void p.subirImagen(archivo);
            e.target.value = '';
          }}
        />

        {/* Panel inferior: tarjetas de valor + actividad reciente. */}
        <div className={estilos.inferior}>
          <div className={estilos.tarjetas}>
            {panel.tarjetas.map((t) => {
              const sensor = p.sensorDe(t.sensorId);
              const vivo = valorEnVivo(sensor);

              const colorValor = !vivo.conDatos
                ? 'var(--dash-tx)'
                : vivo.enAlarma
                  ? '#EF4444'
                  : '#34D399';

              const texto =
                [t.tipo, t.area].filter(Boolean).join(' · ') ||
                t.descripcion ||
                'Clic en ⚙ para elegir sensor';

              return (
                <div
                  key={t.id}
                  className={[estilos.tarjeta, vivo.enAlarma ? estilos.tarjetaAlerta : '']
                    .filter(Boolean)
                    .join(' ')}
                >
                  <div className={estilos.tarjetaEtiqueta}>{t.etiqueta}</div>
                  <div className={estilos.tarjetaValor} style={{ color: colorValor }}>
                    {vivo.conDatos ? vivo.texto : '—'}
                  </div>
                  <div className={estilos.tarjetaTexto}>{texto}</div>

                  <button
                    type="button"
                    className={estilos.insigniaElegir}
                    title="Elegir sensor"
                    onClick={() => setObjetivo({ tipo: 'tarjeta', id: t.id })}
                  >
                    <Icon nombre="ajustes" tamano={11} />
                  </button>
                  <button
                    type="button"
                    className={estilos.insigniaQuitar}
                    title="Eliminar"
                    onClick={() => p.cambiar((a) => a.quitarTarjeta(t.id))}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>

          <div
            className={[estilos.feed, feedPlegado ? estilos.feedPlegado : '']
              .filter(Boolean)
              .join(' ')}
          >
            <button
              type="button"
              className={estilos.feedCabecera}
              onClick={() => setFeedPlegado((v) => !v)}
              aria-expanded={!feedPlegado}
            >
              <span className={estilos.feedTitulo}>Actividad reciente</span>
              <span className={estilos.feedConmutador}>
                <span className={estilos.feedContador}>{feed.length}</span>
                <svg className={estilos.feedFlecha} viewBox="0 0 24 24" aria-hidden>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </button>

            <div className={estilos.feedCuerpo}>
              {alarmas.isLoading ? (
                <div className={estilos.feedVacio}>Cargando alarmas...</div>
              ) : feed.length === 0 ? (
                <div className={estilos.feedVacio}>No hay alarmas activas</div>
              ) : (
                feed.map((a) => {
                  const criticidad = CRITICIDAD_FEED[a.criticidad.valor] ?? CRITICIDAD_FEED_DEFECTO;
                  const claseCrit = criticidad.clase
                    ? estilos[criticidad.clase as keyof typeof estilos]
                    : undefined;

                  return (
                    <div key={a.id} className={estilos.feedElemento}>
                      <div
                        className={estilos.feedIcono}
                        style={{ background: criticidad.fondo, color: criticidad.color }}
                      >
                        <Icon nombre={criticidad.icono} tamano={18} />
                      </div>
                      <div className={estilos.feedPrincipal}>
                        <div className={estilos.feedMensaje}>{a.sensorNombre}</div>
                        <div className={estilos.feedMeta}>
                          <span className={estilos.feedArea}>
                            <Icon nombre="mapa" tamano={11} /> {a.areaNombre ?? '—'}
                          </span>
                          <span className={`${estilos.feedCriticidad} ${claseCrit ?? ''}`}>
                            {a.criticidad.etiqueta}
                          </span>
                        </div>
                        <div className={estilos.feedHora}>{formatearAntiguedad(a.fechaInicio)}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Catálogo de widgets. */}
        {catalogoAbierto && p.editando && (
          <div className={estilos.catalogo}>
            <div className={estilos.catalogoCabecera}>
              <div className={estilos.catalogoTitulo}>Agregar widget</div>
              <button
                type="button"
                className={estilos.catalogoCerrar}
                onClick={() => setCatalogoAbierto(false)}
              >
                ✕
              </button>
            </div>
            <div className={estilos.catalogoCuerpo}>
              {(
                [
                  ['verde', 'Pin sensor', 'Se vuelve rojo si el sensor está en alerta'],
                  ['naranja', 'Pin advertencia', 'Color naranja fijo'],
                  ['azul', 'Pin información', 'Color azul fijo'],
                ] as const
              ).map(([color, nombre, detalle]) => (
                <button
                  key={color}
                  type="button"
                  className={estilos.catalogoElemento}
                  onClick={() => p.cambiar((a) => a.agregarMarcador(color))}
                >
                  <div
                    className={estilos.catalogoMuestra}
                    style={{
                      background: COLOR_MARCADOR[color].fondo,
                      borderColor: COLOR_MARCADOR[color].hex,
                      color: COLOR_MARCADOR[color].hex,
                    }}
                  >
                    ●
                  </div>
                  <div className={estilos.catalogoInfo}>
                    <div className={estilos.catalogoNombre}>{nombre}</div>
                    <div className={estilos.catalogoDetalle}>{detalle}</div>
                  </div>
                </button>
              ))}

              <div className={estilos.catalogoSeparador} />

              <button
                type="button"
                className={estilos.catalogoElemento}
                onClick={() => p.cambiar((a) => a.agregarTarjeta())}
              >
                <div
                  className={estilos.catalogoMuestra}
                  style={{ background: 'var(--dash-glass-b)' }}
                >
                  <span style={{ fontSize: 10, fontWeight: 600 }}>123</span>
                </div>
                <div className={estilos.catalogoInfo}>
                  <div className={estilos.catalogoNombre}>Tarjeta de valor</div>
                  <div className={estilos.catalogoDetalle}>
                    Muestra el valor actual de un sensor
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>

      <SelectorSensor
        abierto={objetivo !== null}
        sensores={p.sensores}
        onCerrar={() => setObjetivo(null)}
        onElegir={elegirSensor}
      />
    </div>
  );
}
